import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { useFocusTrap } from '../hooks/useFocusTrap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface ShareReportModalProps {
  reportId: string;
  reportName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ShareLink {
  share_id: string;
  share_token: string;
  share_url: string;
  expires_at: string;
  watermark_text: string;
  view_count?: number;
  is_expired?: boolean;
}

export default function ShareReportModal({ reportId, reportName, isOpen, onClose }: ShareReportModalProps) {
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<ShareLink | null>(null);
  const [existingShares, setExistingShares] = useState<ShareLink[]>([]);
  const [showExisting, setShowExisting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  // Options
  const [expiresDays, setExpiresDays] = useState(30);
  const [watermarkText, setWatermarkText] = useState('CONFIDENTIAL - READ-ONLY');

  const loadExistingShares = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/public-shares`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingShares(response.data);
      setShowExisting(true);
    } catch (err: any) {
      console.error('Failed to load shares:', err);
    }
  };

  const createShareLink = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/public-share`,
        null,
        {
          params: {
            expires_days: expiresDays,
            watermark_text: watermarkText
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setShareLink(response.data);
      
      // Log share activity
      logActivity({
        reportId,
        actionType: 'share',
        actionDetails: `Created share link for ${reportName}`,
        section: 'sharing',
        metadata: { expires_days: expiresDays, watermark: watermarkText }
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink?.share_url) {
      await navigator.clipboard.writeText(shareLink.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const revokeShare = async (shareId: string) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${BACKEND_URL}/api/v1/collaboration/public-shares/${shareId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExistingShares(prev => prev.filter(s => s.share_id !== shareId));
    } catch (err: any) {
      setError('Failed to revoke share');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Share Report" className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xl">🔗</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Share Report</h2>
                <p className="text-sm text-gray-500">{reportName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Share Link Created */}
          {shareLink ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <span>✅</span> Share Link Created!
                </h3>
                <p className="text-sm text-green-700 mb-3">
                  Anyone with this link can view the report (read-only).
                </p>
                
                {/* Link Display */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    readOnly
                    value={shareLink.share_url}
                    className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm font-mono"
                  />
                  <button
                    onClick={copyToClipboard}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                
                <div className="text-xs text-green-600 space-y-1">
                  <p>📅 Expires: {new Date(shareLink.expires_at).toLocaleDateString()}</p>
                  <p>🔒 Watermark: {shareLink.watermark_text}</p>
                </div>
                
                {/* Email Share Options */}
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-sm font-medium text-green-900 mb-3">📧 Share via Email</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const subject = encodeURIComponent(`Business Interruption Claim Report - ${reportName}`);
                        const body = encodeURIComponent(
                          `I'm sharing a Business Interruption claim report with you for review.\n\n` +
                          `📊 Report: ${reportName}\n` +
                          `🔗 View Report: ${shareLink.share_url}\n\n` +
                          `This is a read-only link that expires on ${new Date(shareLink.expires_at).toLocaleDateString()}.\n\n` +
                          `You can view the full report and download the PDF without needing an account.\n\n` +
                          `---\nSent via DisruptionGenius`
                        );
                        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Open Email Client
                    </button>
                    <button
                      onClick={() => {
                        const emailText = 
                          `Business Interruption Claim Report - ${reportName}\n\n` +
                          `View Report: ${shareLink.share_url}\n\n` +
                          `This read-only link expires on ${new Date(shareLink.expires_at).toLocaleDateString()}.`;
                        navigator.clipboard.writeText(emailText);
                        setStatusMessage('Email text copied! Paste into your email.');
                      }}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                    >
                      Copy Email Text
                    </button>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setShareLink(null);
                  onClose();
                }}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Create New Share */}
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">📤 Generate Share Link</h3>
                  <p className="text-sm text-blue-700">
                    Create a read-only link to share with opposing counsel, adjusters, or other parties.
                    They won't need an account to view.
                  </p>
                </div>
                
                {/* Options */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expires In
                    </label>
                    <select
                      value={expiresDays}
                      onChange={(e) => setExpiresDays(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value={7}>7 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                      <option value={365}>1 year</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Watermark Text
                    </label>
                    <input
                      type="text"
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="CONFIDENTIAL - READ-ONLY"
                    />
                  </div>
                </div>
                
                <button
                  onClick={createShareLink}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Creating...
                    </>
                  ) : (
                    <>🔗 Generate Share Link</>
                  )}
                </button>
              </div>
              
              {/* Existing Shares */}
              <div className="mt-6 pt-6 border-t">
                <button
                  onClick={loadExistingShares}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  {showExisting ? '▼' : '▶'} View Existing Shares ({existingShares.length || '...'})
                </button>
                
                {showExisting && existingShares.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {existingShares.map((share) => (
                      <div
                        key={share.share_id}
                        className={`p-3 rounded-lg border ${
                          share.is_expired 
                            ? 'bg-gray-50 border-gray-200' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <p className={`font-medium ${share.is_expired ? 'text-gray-400' : 'text-gray-700'}`}>
                              {share.is_expired ? '⌛ Expired' : '🔗 Active'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Views: {share.view_count || 0} • 
                              Expires: {new Date(share.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!share.is_expired && (
                            <button
                              onClick={() => revokeShare(share.share_id)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
